import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LeavePayload, LeaveTypeItem, afastamentosDaEquipe, afastamentosDaPessoa, afastamentosPendentes, aprovarAfastamento,
  cancelarAfastamento, listarTiposAfastamento, prorrogarAfastamento, registrarAfastamento, rejeitarAfastamento,
  retornoAntecipado, salvarTipoAfastamento, situacaoTipoAfastamento,
} from '../api/afastamentos';
import { PESSOAS_KEYS } from './index';

export const AF_KEYS = {
  tipos: (all: boolean) => ['pessoas', 'tipos-afastamento', all] as const,
  pessoa: (id: string) => ['pessoas', 'afastamentos', id] as const,
  pendentes: ['pessoas', 'afastamentos', 'pendentes'] as const,
  equipe: ['pessoas', 'afastamentos', 'equipe'] as const,
};

function useWrite<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: PESSOAS_KEYS.all }) });
}

export const useTiposAfastamento = (all = false, enabled = true) =>
  useQuery({ queryKey: AF_KEYS.tipos(all), queryFn: () => listarTiposAfastamento(all), enabled, staleTime: 60_000 });
export const useSalvarTipoAfastamento = () =>
  useWrite(({ id, payload }: { id: string | null; payload: Partial<LeaveTypeItem> }) => salvarTipoAfastamento(id, payload));
export const useSituacaoTipoAfastamento = () =>
  useWrite(({ id, active }: { id: string; active: boolean }) => situacaoTipoAfastamento(id, active));

export const useAfastamentos = (personId: string, enabled = true) =>
  useQuery({ queryKey: AF_KEYS.pessoa(personId), queryFn: () => afastamentosDaPessoa(personId), enabled });
export const useRegistrarAfastamento = (bondId: string) => useWrite((payload: LeavePayload) => registrarAfastamento(bondId, payload));
export const useProrrogarAfastamento = () =>
  useWrite(({ id, expectedEndDate, documentId }: { id: string; expectedEndDate: string; documentId?: string }) =>
    prorrogarAfastamento(id, expectedEndDate, documentId));
export const useRetornoAntecipado = () => useWrite(({ id, endDate }: { id: string; endDate: string }) => retornoAntecipado(id, endDate));
export const useCancelarAfastamento = () =>
  useWrite(({ id, justification }: { id: string; justification: string }) => cancelarAfastamento(id, justification));

export const useAfastamentosPendentes = () => useQuery({ queryKey: AF_KEYS.pendentes, queryFn: afastamentosPendentes });
export const useAprovarAfastamento = () => useWrite((id: string) => aprovarAfastamento(id));
export const useRejeitarAfastamento = () => useWrite(({ id, reason }: { id: string; reason: string }) => rejeitarAfastamento(id, reason));

export const useAfastamentosDaEquipe = () => useQuery({ queryKey: AF_KEYS.equipe, queryFn: afastamentosDaEquipe });
